import React, { useMemo, useState } from 'react';
import { Card, DatePicker, Row, Col, Statistic, Space, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import axios from 'axios';
import { useQuery } from 'react-query';
import { useAuthStore } from '../../stores/authStore';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * 中文函数注释：
 * 调用后端 /overtime/statistics 接口，按日期范围获取当前用户或指定用户的加班统计信息
 */
async function fetchOvertimeStats({ queryKey }) {
  const [_key, { userId, range }] = queryKey;
  const params = {
    userId: userId || undefined,
    startDate: range[0]?.format('YYYY-MM-DD'),
    endDate: range[1]?.format('YYYY-MM-DD')
  };
  const res = await axios.get('/overtime/statistics', { params });
  return res.data.data;
}

/**
 * 中文函数注释：
 * 调用后端 /compensatory/statistics 接口，按日期范围获取当前用户或指定用户的调休统计信息
 */
async function fetchCompStats({ queryKey }) {
  const [_key, { userId, range }] = queryKey;
  const params = {
    userId: userId || undefined,
    startDate: range[0]?.format('YYYY-MM-DD'),
    endDate: range[1]?.format('YYYY-MM-DD')
  };
  const res = await axios.get('/compensatory/statistics', { params });
  return res.data.data;
}

/**
 * 中文函数注释：
 * 页面组件 - 加班与调休综合统计（按用户汇总）
 * 需求变更：不再分别展示“加班明细”和“调休明细”，而是合并为一张表，每行代表一位用户，展示其在所选日期范围内的加班总时长、调休总时长及净剩余。
 */
export default function OvertimeCompStats() {
  const { isAuthenticated } = useAuthStore();
  // 获取日期范围
  const [range, setRange] = useState([dayjs().startOf('month'), dayjs()]);

  // 获取用户列表（受鉴权保护）
  // 中文函数注释：通过 /auth/users 获取全量用户，作为行维度来源
  const usersQuery = useQuery(
    ['users'],
    async () => {
      const res = await axios.get('/auth/users');
      const payload = res.data;
      // 后端标准返回：{ success, data: { users: [], pagination: {...} } }
      const users = Array.isArray(payload?.data?.users)
        ? payload.data.users
        : Array.isArray(payload?.users)
          ? payload.users
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];
      return users;
    },
    { enabled: isAuthenticated, refetchOnWindowFocus: false }
  );

  // 获取所有用户在日期范围内的加班/调休统计并合并为“每行一个用户”的数据
  // 中文函数注释：并发请求每个用户的加班统计与调休统计，计算总时长与净剩余
  const allStatsQuery = useQuery(
    ['overtimeCompAll', { userIds: (Array.isArray(usersQuery.data) ? usersQuery.data : []).map(u => u.id), range }],
    async () => {
      const users = Array.isArray(usersQuery.data) ? usersQuery.data : [];
      const startDate = range[0]?.format('YYYY-MM-DD');
      const endDate = range[1]?.format('YYYY-MM-DD');
      const results = await Promise.all(
        users.map(async (u) => {
          const [ot, comp] = await Promise.all([
            axios.get('/overtime/statistics', { params: { userId: u.id, startDate, endDate } }),
            axios.get('/compensatory/statistics', { params: { userId: u.id, startDate, endDate } })
          ]);
          const overtimeHours = Number(ot?.data?.data?.totalHours || 0);
          const compHours = Number(comp?.data?.data?.totalHours || 0);
          return {
            key: u.id,
            userId: u.id,
            realName: u.realName || u.username || `用户${u.id}`,
            overtimeHours: Number(overtimeHours.toFixed(2)),
            compHours: Number(compHours.toFixed(2)),
            netHours: Number((overtimeHours - compHours).toFixed(2))
          };
        })
      );
      return results;
    },
    { enabled: isAuthenticated && usersQuery.isSuccess && !!range[0] && !!range[1], refetchOnWindowFocus: false }
  );

  // 计算全局总计（所有用户的总和）
  const totals = useMemo(() => {
    const list = allStatsQuery.data || [];
    const overtimeHours = list.reduce((s, r) => s + (Number(r.overtimeHours) || 0), 0);
    const compHours = list.reduce((s, r) => s + (Number(r.compHours) || 0), 0);
    const netHours = overtimeHours - compHours;
    return {
      overtimeHours: Number(overtimeHours.toFixed(2)),
      compHours: Number(compHours.toFixed(2)),
      netHours: Number(netHours.toFixed(2))
    };
  }, [allStatsQuery.data]);

  // 仅保留加班或调休不为0的行
  const filteredData = useMemo(() => {
    const list = allStatsQuery.data || [];
    return list.filter(r => (Number(r.overtimeHours) || 0) !== 0 || (Number(r.compHours) || 0) !== 0);
  }, [allStatsQuery.data]);

  // 合并后单表的列定义
  const columns = [
    { title: '用户', dataIndex: 'realName', key: 'realName' },
    {
      title: '加班总时长(小时)',
      dataIndex: 'overtimeHours',
      key: 'overtimeHours',
      sorter: (a, b) => (a.overtimeHours || 0) - (b.overtimeHours || 0),
      render: (v) => (Number(v) || 0).toFixed(2)
    },
    {
      title: '调休总时长(小时)',
      dataIndex: 'compHours',
      key: 'compHours',
      sorter: (a, b) => (a.compHours || 0) - (b.compHours || 0),
      render: (v) => (Number(v) || 0).toFixed(2)
    },
    {
      title: '净剩余(加班-调休)',
      dataIndex: 'netHours',
      key: 'netHours',
      sorter: (a, b) => (a.netHours || 0) - (b.netHours || 0),
      render: (v) => (
        <Typography.Text style={{ color: (Number(v) || 0) >= 0 ? '#3f8600' : '#cf1322' }}>
          {(Number(v) || 0).toFixed(2)}
        </Typography.Text>
      )
    }
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Title level={4} style={{ margin: 0 }}>加班与调休统计</Title>
          <RangePicker value={range} onChange={(vals) => setRange(vals)} allowClear={false} />
        </Space>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="加班总时长" value={totals.overtimeHours} suffix="小时" precision={2} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="调休总时长" value={totals.compHours} suffix="小时" precision={2} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="净剩余(加班-调休)" value={totals.netHours} suffix="小时" precision={2} valueStyle={{ color: totals.netHours >= 0 ? '#3f8600' : '#cf1322' }} />
          </Card>
        </Col>
      </Row>

      {/* 合并后的单表展示：每行一个用户 */}
      <Card title="加班/调休（按用户汇总）">
        <Table
          rowKey="userId"
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          loading={usersQuery.isLoading || allStatsQuery.isLoading}
          dataSource={filteredData}
          columns={columns}
        />
      </Card>
    </Space>
  );
}